import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["track", "slide", "dots"]
  static values = { page: { type: Number, default: 0 } }

  connect() {
    this.resizeObserver = new ResizeObserver(() => this.render())
    this.resizeObserver.observe(this.element)
    this.element.addEventListener("wheel", this.handleWheel, { passive: false })
    this.element.addEventListener("pointerdown", this.handlePointerDown)
    this.element.addEventListener("pointermove", this.handlePointerMove)
    this.element.addEventListener("pointerup", this.handlePointerUp)
    this.element.addEventListener("pointercancel", this.handlePointerCancel)
    this.element.addEventListener("click", this.preventClickAfterDrag, true)
    this.render()
  }

  disconnect() {
    this.resizeObserver.disconnect()
    this.element.removeEventListener("wheel", this.handleWheel)
    this.element.removeEventListener("pointerdown", this.handlePointerDown)
    this.element.removeEventListener("pointermove", this.handlePointerMove)
    this.element.removeEventListener("pointerup", this.handlePointerUp)
    this.element.removeEventListener("pointercancel", this.handlePointerCancel)
    this.element.removeEventListener("click", this.preventClickAfterDrag, true)
  }

  previous() {
    this.pageValue -= 1
    this.render()
  }

  next() {
    this.pageValue += 1
    this.render()
  }

  handleWheel = (event) => {
    if (this.wheelLocked || Math.abs(event.deltaX) <= Math.abs(event.deltaY) || Math.abs(event.deltaX) < 12) return

    event.preventDefault()
    this.wheelLocked = true
    event.deltaX > 0 ? this.next() : this.previous()
    window.setTimeout(() => { this.wheelLocked = false }, 250)
  }

  handlePointerDown = (event) => {
    this.pointerStartX = event.clientX
    this.dragged = false
  }

  handlePointerMove = (event) => {
    if (this.pointerStartX === undefined || Math.abs(event.clientX - this.pointerStartX) < 24) return

    this.dragged = true
  }

  handlePointerUp = (event) => {
    if (!this.dragged) return this.handlePointerCancel()

    event.clientX < this.pointerStartX ? this.next() : this.previous()
    this.suppressClick = true
    this.handlePointerCancel()
  }

  handlePointerCancel = () => {
    this.pointerStartX = undefined
    this.dragged = false
  }

  preventClickAfterDrag = (event) => {
    if (!this.suppressClick) return

    event.preventDefault()
    event.stopPropagation()
    this.suppressClick = false
  }

  select(event) {
    this.pageValue = Number(event.currentTarget.dataset.carouselPage)
    this.render()
  }

  itemsPerPage() {
    const slideWidth = this.slideTargets[0]?.getBoundingClientRect().width
    const trackWidth = this.trackTarget.getBoundingClientRect().width

    return slideWidth > 0 ? Math.max(1, Math.floor(trackWidth / slideWidth)) : 1
  }

  render() {
    const itemsPerPage = this.itemsPerPage()
    const pageCount = Math.ceil(this.slideTargets.length / itemsPerPage)

    this.pageValue = Math.min(Math.max(this.pageValue, 0), Math.max(pageCount - 1, 0))
    this.trackTarget.style.transform = `translateX(${-this.pageValue * this.trackTarget.parentElement.clientWidth}px)`
    this.element.classList.toggle("carousel--active", pageCount > 1)

    this.slideTargets.forEach((slide, index) => {
      const isInactive = Math.floor(index / itemsPerPage) !== this.pageValue
      slide.setAttribute("aria-hidden", String(isInactive))
      slide.inert = isInactive
    })

    this.dotsTarget.replaceChildren(...Array.from({ length: pageCount }, (_, index) => {
      const dot = document.createElement("button")
      dot.type = "button"
      dot.dataset.action = "carousel#select"
      dot.dataset.carouselPage = index
      dot.setAttribute("aria-current", index === this.pageValue ? "page" : "false")
      dot.setAttribute("aria-label", `Go to carousel page ${index + 1}`)
      return dot
    }))
  }
}
